import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar, Notebook, Users, DollarSign,
  Package, BarChart3, Bell, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { NavItem } from '@/components/atoms';
import { ScripsWordmark } from '@/components/atoms/scrips-wordmark';
import { ProfilePopover } from '@/app/layout/profile-popover';
import { useAuthStore } from '@/auth/auth-store';
import { isNavConfined, visibleNavItems } from '@/app/lib/nav-confinement';
import type { NavId } from '@/app/lib/last-selected-nav';
import { dashboardNavIcon, dashboardNavLabel } from '@/app/lib/dashboard-nav-label';

/**
 * SideNav — Block
 * Left navigation rail for the Scrips application.
 * Fixed 56px width (zone contract Z1 folded rail), no fill of its own — it
 * sits on the app grey (`--surface-app`) painted by the shell. Keyboard navigable.
 *
 * Workflow context: This is the global navigation — every screen gets it.
 * The active state maps to the current route/module.
 *
 * Layer note: imports ProfilePopover from `@/app/layout/`, which is an upward
 * (app-shell) dependency. Pragmatic for now — popover uses auth-store +
 * react-router and isn't a pure presentation primitive. Refactor to a slot
 * prop if a non-app consumer ever needs the block without the popover.
 * `visibleNavItems` / `NavId` come from `@/app/lib/` for the same reason, and
 * `NavId` is now IMPORTED rather than re-declared here — the rail's union and
 * the persistence allow-list were two copies of one list.
 *
 * Role confinement (PROD-2396): the rail is filtered through
 * `visibleNavItems`, which today collapses it to Calendar-only for a
 * Technician and returns every other role's rail unchanged, and the
 * notification bell is dropped for the same users (PROD-2397). The policy
 * lives in `@/app/lib/nav-confinement.ts` — including the reason it is not a
 * security boundary. Do not inline a role id here.
 */

interface SideNavProps {
  /** `null`: the shell is on a page with no rail item (Settings), so nothing is highlighted. */
  activeId?: NavId | null;
  onNavigate?: (id: NavId) => void;
  notificationCount?: number;
  /** Unread patient WhatsApp messages: the badge on the inbox item (the `dashboard` slot). 0 hides it. */
  inboxUnreadCount?: number;
  /**
   * Bell-icon click handler. PROD-868 wires the panel-open trigger in
   * PR 2 (PROD-869); for PR 1 the prop is plumbed but the panel itself
   * is not yet built — callers can pass a placeholder.
   */
  onBellClick?: () => void;
}

// Order matches the Flutter PM sidebar (Samer 2026-05-13). Studio has no
// React surface yet — it falls through to the ComingSoon placeholder in
// App.tsx until the studio_module port lands.
const NAV_ITEMS: { id: NavId; icon: typeof Calendar; label: string }[] = [
  // "Inbox" when the slot shows the WhatsApp inbox (dashboard-nav-label.ts).
  { id: 'dashboard', icon: dashboardNavIcon(), label: dashboardNavLabel() },
  { id: 'calendar', icon: Calendar, label: 'Calendar' },
  { id: 'studio', icon: Notebook, label: 'Studio' },
  { id: 'patients', icon: Users, label: 'Patients' },
  { id: 'reports', icon: BarChart3, label: 'Reports' },
  { id: 'billing', icon: DollarSign, label: 'Billing' },
  { id: 'stock', icon: Package, label: 'Stock' },
  // 'consent' nav removed (PROD-1051 doc-forms) — the template builder moved to
  // Studio › Consent Forms and signing lives in the patient chart's Docs → Forms.
];

/** The user's rail choice, remembered in this browser. */
const RAIL_KEY = 'scrips.rail.expanded';

export function SideNav({ activeId: controlledActive, onNavigate, notificationCount = 0, inboxUnreadCount = 0, onBellClick }: SideNavProps) {
  const [internalActive, setInternalActive] = useState<NavId>('calendar');
  const activeId = controlledActive === undefined ? internalActive : controlledActive;
  // Role-filtered rail. The store selector returns the stable `user` object
  // and the filter runs in render — selecting a freshly-built array straight
  // out of zustand would hand `useSyncExternalStore` a new reference on every
  // snapshot read. A null / unknown user is unconfined, so a bare
  // `<SideNav />` (Storybook, the block's own tests) draws the full list
  // exactly as before.
  const user = useAuthStore((s) => s.user);
  const navItems = visibleNavItems(NAV_ITEMS, user);
  const confined = isNavConfined(user);

  // Open (labels beside the icons, 232px) or folded (icons, 56px): the user's choice, remembered,
  // toggled on the logo or with `[` (Antonin, 12 Oct, both apps). Everything is anchored to the left
  // edge in both states, so the logo, icons and avatar never move while the width animates.
  // Opens expanded by default; only an explicit fold ('0') is remembered as folded.
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(RAIL_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const toggle = () =>
    setExpanded((v) => {
      const next = !v;
      try {
        localStorage.setItem(RAIL_KEY, next ? '1' : '0');
      } catch {
        /* not remembered */
      }
      return next;
    });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey || t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // One delegated tooltip for the destinations and the folded logo (Antonin, 12 Oct): shows at once,
  // no delay or fade, and stays up while the pointer moves from one item to the next. Inverted
  // (`--tooltip-bg` / `--tooltip-fg`), 4px from the icon's tile. None when the rail is open.
  const [tip, setTip] = useState<{ text: string; top: number } | null>(null);
  const showTip = (target: EventTarget | null) => {
    if (expanded) {
      setTip(null);
      return;
    }
    const el = target as HTMLElement | null;
    const btn = el?.closest?.('[data-nav-destination], [data-rail-tip]') as HTMLElement | null;
    if (!btn) {
      if (!el?.closest?.('[data-nav-destinations]')) setTip(null);
      return;
    }
    const r = btn.getBoundingClientRect();
    setTip({ text: btn.dataset.railTip ?? btn.dataset.navDestination ?? '', top: r.top + r.height / 2 });
  };

  const handleNav = (id: NavId) => {
    if (onNavigate) {
      onNavigate(id);
    } else {
      setInternalActive(id);
    }
  };

  return (
    <nav
      className={`${expanded ? 'w-[var(--zone-rail-expanded)]' : 'w-14'} flex flex-shrink-0 flex-col items-stretch gap-2 px-2 pb-4 transition-[width] duration-200 motion-reduce:transition-none`}
      aria-label="Main navigation"
      data-rail={expanded ? 'expanded' : 'collapsed'}
      onMouseOver={(e) => showTip(e.target)}
      onMouseLeave={() => setTip(null)}
      onMouseDown={() => setTip(null)}
      onFocus={(e) => showTip(e.target)}
      onBlur={() => setTip(null)}
    >
      {/*
       * Brand mark — the Signal DS component, not a local tile.
       *
       * This was a hand-built `bg-brand-primary` square with a bold letter "S"
       * in it. The DS already owns this exact 32×32 element: `ScripsBrandMark`
       * is the Figma "Images/Branding/PracticeIcon" squircle carrying the white
       * "ScripsIcon" glyph, with its fill bound to `--color-brand-primary`
       * (the Figma instance fills it `#0082ca`, a REJECTED hex, so the token is
       * the truth). A letter is not the logo, and a second local copy of a
       * graduated component is how brand drift starts.
       *
       * `mb-4` stays on a wrapper rather than being passed through `className`:
       * the mark's own class list sets its size and radius, and rail spacing is
       * the rail's business, not the mark's.
       */}
      {/* Zone contract Z1: the logo sits in a 48px slot level with the
          GlobalBar, so with the 8px gap the first item starts 56px down. */}
      {/* The top of the rail, identical in both apps (Antonin, 19 Oct: "the logo in the closed rail should be full
          size, same as when open, and the expand control next to it"): the full wordmark (32 tall, x 12) and the 32px
          toggle 8 to its right, in the same place folded and open. Folded, the row runs past the 56px rail and the top
          bar's Back + crumbs start after it (showcase-bridge.css, `--rail-brand-push`). */}
      <div className="relative z-10 flex h-[var(--zone-global-bar)] w-max flex-shrink-0 items-center gap-[var(--rail-brand-gap-x)] self-start pl-1">
        <ScripsWordmark color="var(--rail-wordmark)" />
        <button
          type="button"
          onClick={toggle}
          aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
          aria-expanded={expanded}
          aria-keyshortcuts="["
          data-rail-toggle
          className="grid size-[var(--rail-toggle-size)] shrink-0 place-items-center rounded-[var(--radius-control)] text-[var(--ink-meta)] hover:bg-[var(--surface-read)] hover:text-[var(--ink-body)]"
        >
          {expanded ? <PanelLeftClose className="size-5" aria-hidden /> : <PanelLeftOpen className="size-5" aria-hidden />}
        </button>
      </div>

      <div data-nav-destinations className="flex flex-col items-stretch gap-2">
        {navItems.map((item) => (
          <NavItem
            key={item.id}
            icon={item.icon}
            label={item.label}
            destination
            expanded={expanded}
            isActive={activeId === item.id}
            badge={item.id === 'dashboard' ? inboxUnreadCount || undefined : undefined}
            onClick={() => handleNav(item.id)}
          />
        ))}
      </div>

      <div className="flex-1" />

      {/*
        * The bell is REMOVED for a confined role (PROD-2397), not merely
        * route-guarded, and the distinction is the whole point. Two reasons
        * a guard would not have been enough:
        *
        *  1. The requirement is "only the device screen". A notification
        *     bell is not the device screen. Hiding it is the faithful
        *     reading, not scope creep.
        *  2. The panel RENDERS patient-identifying text. A route guard
        *     inspects URLs; it never touches rendered content. Notification
        *     rows do navigate to `/scheduling/appointment/:id`, and the
        *     guard does bounce that — but bouncing the click while still
        *     painting the patient's name in the list is not a control.
        *
        * Removing the trigger is half of it: `App.tsx` also declines to
        * MOUNT `NotificationPanel` for a confined user, because
        * `useUserNotifications` has no `enabled` guard and would otherwise
        * pull the whole list into the React Query cache for someone who can
        * never open it. Same hard-unmount reasoning as the worklist and
        * Order Requests tabs.
        *
        * Still not a security boundary — the notifications endpoint
        * enforces no role either (PROD-2388).
        */}
      {!confined && (
        <NavItem
          icon={Bell}
          label="Notifications"
          expanded={expanded}
          badge={notificationCount || undefined}
          onClick={onBellClick}
        />
      )}
      {/* Same box in both states: the avatar never moves when the rail opens. */}
      <div className="flex h-8 w-full items-center pl-1">
        <ProfilePopover expanded={expanded} />
      </div>

      {tip &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed left-[var(--rail-tip-left)] z-[300] -translate-y-1/2 whitespace-nowrap rounded-input bg-[var(--tooltip-bg)] px-2 py-1 text-xs font-medium text-[var(--tooltip-fg)] shadow-overlay"
            // top follows the hovered item; measured at runtime, not a design value.
            style={{ top: tip.top }}
          >
            {tip.text}
          </div>,
          document.body,
        )}
    </nav>
  );
}
